import type { ComponentPropsWithoutRef } from "react";
import { cn } from "@/lib/utils";
import { renderRichTextField } from "@/lib/storyblok/utils";
import type { RichtextStoryblok } from "@/component-types-sb";

type Props = ComponentPropsWithoutRef<"div"> & {
  value: string | RichtextStoryblok | undefined;
};

/**
 * Renders a Storyblok field that may be either a plain string (legacy)
 * or a richtext object. Handles backward compatibility automatically.
 */
const RichTextField = ({ value, className, ...rest }: Props) => {
  if (!value) return null;

  // Plain string — render as-is with whitespace preserved
  if (typeof value === "string") {
    return (
      <p className={className} style={{ whiteSpace: "pre-line" }} {...rest}>
        {value}
      </p>
    );
  }

  // Richtext object — render as HTML
  const html = renderRichTextField(value);
  if (!html) return null;

  return (
    <div
      className={cn(
        // Links: underlined, inherit color, pointer cursor
        "[&_a]:underline [&_a]:underline-offset-2 [&_a]:cursor-pointer",
        // Lists
        "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
        // Remove first/last paragraph margins for tight layouts
        "[&>p:first-child]:mt-0 [&>p:last-child]:mb-0",
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
      {...rest}
    />
  );
};

export { RichTextField };
